import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css, keyframes } from '@emotion/react';
import { useCallback, useMemo } from 'react';
import { useSocketSend } from '../../context/webSocket/webSocketContext';
import { useAppSelector } from '../../redux/store';
import { DRAGGABLE_BOMB_BUTTON_ACTIVE_COLOR, DRAGGABLE_BOMB_BUTTON_BACKGROUND_COLOR, DRAGGABLE_BOMB_BUTTON_HOVER_COLOR, PLAYER_INFO_PANEL_SECTION_BUTTON_COLOR, SELF_PANEL_BUTTON_ACTIVE_COLOR, SELF_PANEL_BUTTON_HOVER_COLOR } from '../../styles/colors';
import { ALL_BOMB_PROPERTIES } from '../../types/bombTypes';
import { throwBomb } from '../../utils/bombUtils';
import { ActionCostDisplay } from './actionCostDisplay';
const BOMB_BUTTON_SIZE_PX = 50;
const CONTAINER_CSS = css `
  position: relative;
  user-select: none;
`;
const DISABLED_IMAGE_CSS = css `
  opacity: 0.35;
`;
const LOADING_KEYFRAMES = keyframes `
  0% {
    opacity: 0.2;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 0.2;
  }
`;
const LOADING_IMAGE_CSS = css `
  animation: ${LOADING_KEYFRAMES} 2s infinite;
`;
export const BombButton = (props) => {
    const { mode, bombProperties } = props;
    const username = 'username' in props ? props.username : undefined;
    const socketSend = useSocketSend();
    const canAffordBomb = useAppSelector((state) => (state.selfData.numJacks || 0) + (state.selfData.numAmmo || 0) >= bombProperties.bombCost);
    const isBombLoaded = useAppSelector((state) => !!state.session.loadedBombs[bombProperties.bombType]);
    const isDisabled = !canAffordBomb || !isBombLoaded;
    const onClick = useCallback(() => {
        if (isDisabled) {
            return;
        }
        if (mode === 'draggable') {
            return;
        }
        if (!username) {
            return;
        }
        throwBomb({
            targetUsername: username,
            bombType: bombProperties.bombType,
            uiControlType: 'player panel',
            socketSend
        });
    }, [bombProperties.bombType, isDisabled, mode, socketSend, username]);
    const { bombType, bombCost, label } = bombProperties;
    const getTitle = () => {
        if (!isBombLoaded) {
            return 'Loading';
        }
        if (!canAffordBomb) {
            return 'Not enough Ammo or Jacks';
        }
        if (mode === 'draggable') {
            return `Drag ${label} to player`;
        }
        return `Use ${label} on ${props.isSelf ? 'yourself' : props.username}`;
    };
    const getImageCss = () => {
        if (!isBombLoaded) {
            return LOADING_IMAGE_CSS;
        }
        if (isDisabled) {
            return DISABLED_IMAGE_CSS;
        }
    };
    const buttonCss = useMemo(() => {
        const baseButtonCss = css `
      padding: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: ${mode === 'draggable'
            ? DRAGGABLE_BOMB_BUTTON_BACKGROUND_COLOR
            : PLAYER_INFO_PANEL_SECTION_BUTTON_COLOR};
      border-radius: 10px;
      border: none;
    `;
        if (isDisabled) {
            return baseButtonCss;
        }
        return css(baseButtonCss, css `
        cursor: pointer;
        &:hover {
          background-color: ${mode === 'draggable' ? DRAGGABLE_BOMB_BUTTON_HOVER_COLOR : SELF_PANEL_BUTTON_HOVER_COLOR};
        }
        &:active {
          background-color: ${mode === 'draggable'
            ? DRAGGABLE_BOMB_BUTTON_ACTIVE_COLOR
            : SELF_PANEL_BUTTON_ACTIVE_COLOR};
        }
        img {
          filter: drop-shadow(4px 4px 0 rgba(0, 0, 0, 0.15));
        }
      `, mode === 'draggable' &&
            css `
          /**
           * touch-action 'none' prevents the container from scrolling when you start to drag a bomb
           * (which is a big problem on mobile, preventing you from effectively dragging bombs if there are
           * enough bomb buttons in the container to make it scrollable)
           */
          touch-action: none;
        `);
    }, [isDisabled, mode]);
    const bombCostCss = useMemo(() => css `
      position: absolute;
      bottom: 0;
      right: 0;
      padding: 0 4px;
      background-color: ${mode === 'draggable' ? DRAGGABLE_BOMB_BUTTON_ACTIVE_COLOR : SELF_PANEL_BUTTON_HOVER_COLOR};
      border-bottom-right-radius: 10px;
      border-top-left-radius: 10px;
      pointer-events: none;
    `, [mode]);
    return (_jsxs("div", { css: CONTAINER_CSS, children: [_jsx("button", { onClick: mode === 'targeted' ? onClick : undefined, css: buttonCss, title: getTitle(), disabled: isDisabled, "data-testid": 'bomb-button', children: _jsx("img", { css: getImageCss(), src: ALL_BOMB_PROPERTIES[bombType].buttonImageUrl, width: BOMB_BUTTON_SIZE_PX, height: BOMB_BUTTON_SIZE_PX, draggable: false }) }), _jsx("div", { css: bombCostCss, children: _jsx(ActionCostDisplay, { value: bombCost, canAfford: canAffordBomb }) })] }));
};
